import type { IntlShape } from 'react-intl';
import { createIntl, createIntlCache } from 'react-intl';

import { getLoadedLocaleMessages } from '~/lib/localeMessages';
import { store } from '~/store/store';

const cache = createIntlCache();
const intlByLocale = new Map<string, IntlShape>();

/**
 * Default browser tab title. Used for the root route and as the fallback and
 * suffix in `pageTitle`.
 */
export const DEFAULT_DOCUMENT_TITLE = 'smartlearn';

/**
 * Builds a browser tab title from page-specific segments, always suffixed with
 * the app name — e.g. `pageTitle('Dashboard')` → `Dashboard · smartlearn`.
 * Empty segments are dropped, so `pageTitle()` falls back to the app name alone.
 */
export function pageTitle(
    ...segments: Array<string | null | undefined>
): string {
    return [...segments, DEFAULT_DOCUMENT_TITLE].filter(Boolean).join(' · ');
}

/**
 * Provides an `IntlShape` for use outside the React tree — specifically in route
 * module `meta` exports, which run outside `IntlProvider`. Reads the active
 * locale from the store and the same compiled messages the `LanguageWrapper`
 * uses, memoised per locale.
 *
 * The catalogs are fetched on demand (see `lib/localeMessages`), so until the
 * active locale has loaded this returns an `IntlShape` without messages and
 * titles fall back to the plain app name. The document title is resolved at
 * navigation time, so switching the language while staying on a page is
 * reflected on the next navigation.
 */
export function getMetaIntl(): IntlShape {
    const { locale } = store.getState().locale;

    let intl = intlByLocale.get(locale);
    if (!intl) {
        const messages = getLoadedLocaleMessages(locale);

        intl = createIntl(
            {
                locale,
                messages: (messages ?? {}) as Record<string, string>,
            },
            cache,
        );

        // Only memoise once the catalog is available, so early calls without
        // messages are retried on the next navigation.
        if (messages) {
            intlByLocale.set(locale, intl);
        }
    }

    return intl;
}

/**
 * Resolves a message id to its translation for the active locale, or `null`
 * when the catalog has no such message.
 */
export function metaMessage(id: string): string | null {
    const intl = getMetaIntl();

    return intl.messages[id] != null
        ? String(intl.formatMessage({ id }))
        : null;
}
